import { KeyboardEvent } from '@react-types/shared';
import React, { FocusEvent, forwardRef, MutableRefObject, useCallback, useState, ClipboardEvent, useRef } from 'react';
import { genericMemo, useDebounce, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { useIsSSR } from 'react-aria';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { TextAreaProps as TextAreaBaseProps } from '../TextInput/types';
import { useTextAreaAutosize } from '../../hooks/useTextAreaAutosize';
import { useObjectRef } from '../../hooks/useObjectRef';
import { getCurrentBrowser } from '../../lib/useragent/getCurrentBrowser/getCurrentBrowser';
import { getCurrentPlatform } from '../../lib/useragent/getCurrentPlatform/getCurrentPlatform';

import { setFocusAtEnd } from './setFocusAtEnd';

import './TextArea.css';

const cls = cn('TextArea');

export interface TextAreaProps extends TestProps, TextAreaBaseProps {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Действие при прокрутке
     */
    onScroll?: () => void;
}

/**
 * Поиск курсора
 * @param container - поле, в котором осуществляется поиск
 */
export const getSelection = (container: Element) => {
    const selection = typeof window === 'undefined' ? null : window.getSelection();

    if (!selection || !selection.rangeCount) {
        return;
    }

    const range = selection.getRangeAt(0);
    const isSelectionInContainer =
        range.commonAncestorContainer === container || container.contains(range.commonAncestorContainer);

    if (!isSelectionInContainer) {
        return;
    }

    const length = selection.toString().length;
    const { startContainer, startOffset, endContainer, endOffset } = range;

    range.setStart(container, 0);
    range.setEnd(startContainer, startOffset);
    selection.removeAllRanges();
    selection.addRange(range);

    const startFromContainer = selection.toString().length;

    range.setStart(startContainer, startOffset);
    range.setEnd(endContainer, endOffset);
    selection.removeAllRanges();
    selection.addRange(range);

    return {
        start: startFromContainer,
        length: length,
    };
};

/**
 * Установка курсора
 * @param container - поле, в котором осуществляется вставка
 * @param selection - место, в которое следует поместить курсор
 */
export const setSelection = (container: Node, selection: { start: number; length: number }) => {
    const currentSelection = typeof window === 'undefined' ? null : window.getSelection();

    if (!currentSelection) {
        return;
    }

    let charIndex = 0;
    const range = document.createRange();
    range.setStart(container, 0);
    range.collapse(true);
    const nodeStack = [container];
    let node;
    let foundStart = false;
    let stop = false;

    const start = selection.start;
    const end = start + selection.length;

    // проверка дополнительного условия из-за специфики работы Firefox на Windows
    // дополнительная информация по проблеме: https://st.yandex-team.ru/DIRECT-180334
    const shouldIncreaseBrLength = getCurrentPlatform() === 'Windows' && getCurrentBrowser() === 'Firefox';
    const brTagLength = shouldIncreaseBrLength ? 2 : 1;

    while (!stop && (node = nodeStack.pop())) {
        if (node instanceof Text || node.nodeName === 'BR') {
            const nodeTextLength = node instanceof Text ? node.length : brTagLength;
            const nextCharIndex = charIndex + nodeTextLength;
            if (!foundStart && start >= charIndex && start <= nextCharIndex) {
                node.nodeName === 'BR' ? range.setStartAfter(node) : range.setStart(node, start - charIndex);
                foundStart = true;
            }

            if (foundStart && end >= charIndex && end <= nextCharIndex) {
                node.nodeName === 'BR' ? range.setEndAfter(node) : range.setEnd(node, end - charIndex);
                stop = true;
            }
            charIndex = nextCharIndex;
        } else {
            let i = node.childNodes.length;
            while (i--) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO DIRECT-222878 Указать причину
                nodeStack.push(node.childNodes[i]!);
            }
        }
    }

    currentSelection.removeAllRanges();
    currentSelection.addRange(range);
};

/**
 * Получение корректного текста из поля ввода (без стилей и лишних нод)
 * @param element - элемент, в который вводится текст
 */
function getPlainText(element: HTMLElement) {
    const nodes = element.childNodes;

    // проверка на фактическую пустоту поля
    if (nodes.length <= 1 && nodes[0] && (nodes[0].nodeName === 'BR' || nodes[0].textContent?.trim().length === 0)) {
        return '';
    }

    return element.innerText;
}

/**
 * Создание новой строки (корректирует поведение в разных браузерах)
 * @param element - элемент, в который вводится текст
 */
function createNewLineBreak(element: HTMLElement) {
    const selection = typeof window === 'undefined' ? null : window.getSelection();
    if (!selection) {
        return;
    }

    let isCursorAtEnd = false;

    // проверка, что курсор находится в конце текста
    if (selection.rangeCount && selection.anchorNode) {
        const range = selection.getRangeAt(0);
        const testRange = range.cloneRange();

        const isAnchorContainer = selection.anchorNode === element;
        const isAnchorLastChild = selection.anchorNode === element.lastChild;

        // узел, где находится курсор может быть либо последним дочерним элементом, либо самим контейнером
        const nodeToSelect = isAnchorContainer ? element : selection.anchorNode;

        testRange.selectNodeContents(nodeToSelect);
        testRange.setStart(range.endContainer, range.endOffset);

        isCursorAtEnd = testRange.toString() === '' && (isAnchorLastChild || isAnchorContainer);
    }

    // проверка, сколько переносов строки нужно сделать, чтобы фактически получить одну новую строку
    const isSafari = getCurrentBrowser() === 'Safari';
    let shouldPasteDoubleBr = false;
    if (
        (!isSafari && element.lastChild?.nodeName !== 'BR' && isCursorAtEnd) ||
        (isSafari && (!element.innerText || (isCursorAtEnd && !element.innerText?.endsWith('\n\n'))))
    ) {
        shouldPasteDoubleBr = true;
    }

    // добавление новой строки
    const br = document.createElement('br');
    const extraBr = document.createElement('br');
    const range = selection.getRangeAt(0);

    range.deleteContents();

    if (shouldPasteDoubleBr) {
        range.insertNode(extraBr);
    }

    range.insertNode(br);

    range.setStartAfter(br);
    range.setEndAfter(br);
    range.collapse(false);

    selection.removeAllRanges();
    selection.addRange(range);

    // прокрутка до новой строки (нужна потому, что перезаписывается поведение по-умолчанию при нажатии на Enter)
    const lineHeight = parseInt(window.getComputedStyle(element).getPropertyValue('line-height'));
    element.scrollTop += lineHeight;
}

/**
 * Обновление текста в поле ввода с форматированием и установкой курсора
 * @param element - элемент, в который вводится текст
 * @param options - дополнительные параметры про состояние поля ввода
 * @returns string - строка обновленного текста в поле ввода
 */
function updateInputValue(
    element: HTMLElement,
    {
        restrictNewlines,
        isFocused,
    }: {
        restrictNewlines: boolean;
        isFocused: boolean;
    },
) {
    // получение "чистого" текста и положения курсора
    let value = getPlainText(element);
    const selection = isFocused && getSelection(element);

    if (restrictNewlines) {
        if (selection) {
            /* находим все пары переносов строк, включая пересекающиеся */
            const match = value.match(/\n{2}/g);
            if (match) {
                /* поскольку мы планируем удалять все переносы строк
                 * и заменять любое их количество подряд всего на один пробел
                 * нам необходимо скорректировать текущее выделение текста */
                selection.start -= match.length - 1;
            }
        }
        value = value.replace(/\n+/g, ' ');
    }

    // обновление текста и последующая установка курсора (чтобы он не слетел в другое место)
    element.innerText = value;
    if (selection) {
        setSelection(element, selection);
    }

    return value;
}

/**
 * Обработка замены выделенного текста на новый вставленный
 * @param textToPaste - текст, который вставляется в поле ввода
 */
function handlePasteEvent(textToPaste: string) {
    const selection = typeof window === 'undefined' ? null : window.getSelection();

    if (!selection) {
        document.execCommand('insertText', false, textToPaste);
        return;
    }

    if (!selection.rangeCount) {
        return;
    }

    const textNode = document.createTextNode(textToPaste);
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(textNode);
    range.selectNodeContents(textNode);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
}

/**
 * Возвращает текст поля ввода после вставки в него нового значения с учетом выделенного текста
 * @param element - поле ввода, в которое вводится текст
 * @param textToPaste - текст, который вставляется в поле ввода
 * @returns string - текст поля ввода с учетом замены выделенного текста на новый
 */
function getTextAfterPaste(element: HTMLElement, textToPaste: string) {
    const value = getPlainText(element);
    const rangeSelection = getSelection(element);

    if (!rangeSelection) {
        return;
    }

    const { start, length } = rangeSelection;

    return value.substring(0, start) + textToPaste + value.substring(start + length, value.length);
}

/**
 * Компонент для создания многострочных полей ввода
 * @internal - компонент для создания TextInput с множеством строк
 */

export const TextArea = genericMemo(
    forwardRef<HTMLDivElement, TextAreaProps>(function TextArea(props, forwardedRef) {
        const {
            className,
            id,
            inputMode = 'text',
            onChange: _onChange,
            onFocus,
            onBlur,
            onPaste,
            onScroll,
            onKeyDown,
            onKeyUp,
            isDisabled = false,
            validationState,
            placeholder,
            value,
            maxLength,
            sizeControlType = 'max',
            textAreaRows = 1,
            restrictNewlines = false,
            ...otherProps
        } = props;

        const isSSR = useIsSSR();

        const textAreaRef = useObjectRef(forwardedRef);

        // управление фокусом
        const [_isFocused, setIsFocused] = useState(false);
        // Отключенный элемент не может быть сфокусированным
        const isFocused = !isDisabled && _isFocused;
        const isSetFocusAtEnd = useRef(false);

        // обновление компонента при наличии значения по умолчанию или при изменении извне
        const updateValue = useCallback(() => {
            if (!textAreaRef.current) {
                return;
            }

            if (value !== textAreaRef.current.innerText) {
                textAreaRef.current.textContent = value || '';
                textAreaRef.current.innerText = value || '';

                if (isFocused) {
                    isSetFocusAtEnd.current = true;
                    setFocusAtEnd(textAreaRef.current);
                }
            }

            if (isFocused && !isSetFocusAtEnd.current) {
                isSetFocusAtEnd.current = true;
                setFocusAtEnd(textAreaRef.current);
            }
        }, [isFocused, textAreaRef, value]);
        useIsomorphicLayoutEffect(updateValue, [updateValue]);

        // Обновляем текст внутри textarea если value не изменилось после ввода.
        // Делаем это после синхронных изменений
        const debouncedUpdateValue = useDebounce(updateValue, 0);

        const onChange = useCallback(
            (value: string) => {
                debouncedUpdateValue();
                _onChange?.(value);
            },
            [_onChange, debouncedUpdateValue],
        );

        const onFocusHandler = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                setIsFocused(true);
                onFocus?.(event);
            },
            [onFocus],
        );

        const onBlurHandler = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                onBlur?.(event);
            },
            [onBlur],
        );

        const onInputHandler = useCallback(() => {
            if (!textAreaRef.current) {
                return;
            }

            const value = updateInputValue(textAreaRef.current, {
                isFocused,
                restrictNewlines,
            });

            onChange?.(value);
        }, [isFocused, onChange, textAreaRef, restrictNewlines]);

        const onPasteHandler = useCallback(
            (event: ClipboardEvent<HTMLInputElement>) => {
                if (!textAreaRef.current || isDisabled) {
                    return;
                }

                event.preventDefault();

                const text = event.clipboardData.getData('text/plain');

                if (onPaste) {
                    onPaste(event, getTextAfterPaste(textAreaRef.current, text));

                    return;
                }

                handlePasteEvent(text);

                const value = updateInputValue(textAreaRef.current, {
                    isFocused,
                    restrictNewlines,
                });

                onChange?.(value);
            },
            [textAreaRef, onPaste, onChange, isFocused, restrictNewlines, isDisabled],
        );

        // автоматическое растягивание textarea и контейнера
        useTextAreaAutosize(
            {
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                ref: textAreaRef as MutableRefObject<HTMLDivElement>,
                rows: textAreaRows,
                sizeControlType: sizeControlType,
                isEnabled: true,
            },
            [value],
        );

        // корректирование создания новых строк
        const onKeyDownHandler = useCallback(
            (event: KeyboardEvent) => {
                if (event.keyCode === 13 && textAreaRef.current) {
                    event.preventDefault();
                    if (!restrictNewlines) {
                        createNewLineBreak(textAreaRef.current);
                        onInputHandler();
                    }
                }

                onKeyDown?.(event);
            },
            [textAreaRef, onKeyDown, restrictNewlines, onInputHandler],
        );

        return (
            <div
                id={id}
                className={cls('textarea', { disabled: isDisabled }, [className])}
                ref={textAreaRef}
                role="textbox"
                inputMode={inputMode}
                aria-multiline="true"
                contentEditable={!isDisabled}
                suppressContentEditableWarning
                placeholder={placeholder}
                onFocus={onFocusHandler}
                onBlur={onBlurHandler}
                onInput={onInputHandler}
                onScroll={onScroll}
                onKeyDown={onKeyDownHandler}
                onKeyUp={onKeyUp}
                onPaste={onPasteHandler}
                data-testid={props['data-testid']}
                // Подставляем значение при SSR, а после гидратации контроллируем содержимое через useLayoutEffect
                children={isSSR ? value : undefined}
                {...otherProps}
            />
        );
    }),
);
